import { Component } from '@theme/component';
import { CustomizerPanelController } from '@theme/customizer-panel-controller';

// Internal canvas resolution (square). Fixed smaller than the live customizer's
// 2000x2000 to bound memory across many preview cards. The ctx is scaled so
// window.updateCanvas can draw in its native 2000x2000 coordinate space.
const CUSTOMIZER_COORD_SPACE = 2000;
const PREVIEW_INTERNAL_SIZE = 800;
const PREVIEW_TEXT_STORAGE_KEY = 'personalized-grid:preview-texts';

// Allowlist of characters that can actually be printed on a label.
// Strips emojis and other unsupported glyphs at input time so the canvas
// preview, localStorage bridge, and downstream Vue customizer never see them.
const PRINTABLE_REGEX =
  /[^A-Za-z0-9àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸåÅ\s+\-_/\\\[\]=.*!?,:;(){}<>"'|$#@%^&]/g;

// iOS/macOS smart punctuation rewrites a typed ' into ’ and " into “/”, which
// the allowlist above would then silently delete — the apostrophe looks
// "blocked" to the shopper. Fold typographic glyphs back to ASCII first.
// Keep in sync with snippets/vue-script.liquid.
const SMART_PUNCTUATION = {
  '‘': "'", '’': "'", '‚': "'", '‛': "'",
  'ʹ': "'", 'ʼ': "'", 'ˈ': "'", '´': "'", '`': "'",
  '“': '"', '”': '"', '„': '"', '‟': '"', '″': '"',
  '‐': '-', '‑': '-', '‒': '-', '–': '-', '—': '-', '―': '-',
  '…': '...',
};
const SMART_PUNCTUATION_REGEX = /[‘’‚‛ʹʼˈ´`“”„‟″‐-―…]/g;

function sanitizePrintable(value) {
  if (typeof value !== 'string') return '';
  return value.replace(SMART_PUNCTUATION_REGEX, (char) => SMART_PUNCTUATION[char]).replace(PRINTABLE_REGEX, '');
}

function readStoredPreviewTexts() {
  try {
    const raw = localStorage.getItem(PREVIEW_TEXT_STORAGE_KEY);
    if (!raw) return { Line_1: '', Line_2: '' };
    const parsed = JSON.parse(raw);
    return {
      Line_1: typeof parsed.Line_1 === 'string' ? parsed.Line_1 : '',
      Line_2: typeof parsed.Line_2 === 'string' ? parsed.Line_2 : '',
    };
  } catch {
    return { Line_1: '', Line_2: '' };
  }
}

function writeStoredPreviewTexts(texts) {
  try {
    localStorage.setItem(PREVIEW_TEXT_STORAGE_KEY, JSON.stringify(texts));
  } catch {
    /* quota or disabled — ignore */
  }
}

class PersonalizedGridComponent extends Component {
  #observer = null;
  #clickDelegate = null;
  #panelController = null;
  /** @type {Map<HTMLElement, {ctx: CanvasRenderingContext2D, label: Object, selection: Object, texts: Object, photo: Object}>} */
  #cardRenderStates = new Map();
  #previewInputHandler = null;
  #previewInputDebounce = null;
  #loadMoreObserver = null;
  #isLoadingMore = false;

  connectedCallback() {
    super.connectedCallback();
    this.#panelController = new CustomizerPanelController(this, {
      onPanelLoaded: (vueSectionId) => this.#pushStoredTextsToVueApp(vueSectionId),
    });
    this.#panelController.connect();
    this.#setupPreviewObserver();
    this.#wireOpenTriggers();
    this.#wirePreviewInputs();
    this.#wireLoadMore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#loadMoreObserver?.disconnect();
    this.#panelController?.disconnect();
    if (this.#clickDelegate) this.removeEventListener('click', this.#clickDelegate);
    if (this.#previewInputHandler) {
      this.querySelectorAll('[data-preview-line]').forEach((inp) =>
        inp.removeEventListener('input', this.#previewInputHandler)
      );
    }
    clearTimeout(this.#previewInputDebounce);
    this.#cardRenderStates.clear();
  }

  // --- Lazy preview rendering ---

  #setupPreviewObserver() {
    if (typeof IntersectionObserver === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const rootMargin = isMobile ? '100px 0px' : '200px 0px';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#observer.unobserve(entry.target);
          this.#queueRender(entry.target);
        }
      },
      { rootMargin, threshold: 0.01 }
    );

    this.querySelectorAll('.personalized-grid__card').forEach((card) => this.#observer.observe(card));
  }

  // --- Load more on scroll ---

  #wireLoadMore() {
    const link = this.querySelector('[data-load-more]');
    if (!link) return;

    link.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      this.#loadNextPage(link, { moveFocus: true });
    });

    if (typeof IntersectionObserver === 'undefined') return;

    this.#loadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadNextPage(link);
      },
      { rootMargin: '600px 0px' }
    );
    this.#loadMoreObserver.observe(link);
  }

  /**
   * Appends the next page's cards. A failed load stops the scroll trigger and
   * leaves the link in place, so the shopper can still click for the next page.
   */
  async #loadNextPage(link, { moveFocus = false } = {}) {
    if (this.#isLoadingMore) return;
    this.#isLoadingMore = true;
    const label = link.textContent;
    link.textContent = link.dataset.loadingLabel;
    link.setAttribute('aria-busy', 'true');
    link.setAttribute('aria-disabled', 'true');

    try {
      const url = new URL(link.href);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = this.querySelector('.personalized-grid__list');
      const items = [...page.querySelectorAll('.personalized-grid__list > [role="listitem"]')].map((item) =>
        document.importNode(item, true)
      );

      for (const item of items) {
        list.append(item);
        this.#runInlineScripts(item);
        const card = item.querySelector('.personalized-grid__card');
        if (card) this.#observer?.observe(card);
      }

      if (moveFocus) items[0]?.querySelector('a')?.focus();

      const nextUrl = page.querySelector('[data-load-more]')?.getAttribute('href');
      if (nextUrl) {
        link.href = nextUrl;
        // Observing again reports the link straight away if it's still near the viewport.
        this.#loadMoreObserver?.unobserve(link);
        this.#loadMoreObserver?.observe(link);
      } else {
        this.#loadMoreObserver?.disconnect();
        link.closest('.personalized-grid__load-more')?.remove();
      }
    } catch (error) {
      console.warn('[personalized-grid] Could not load more products', error);
      this.#loadMoreObserver?.disconnect();
    } finally {
      link.textContent = label;
      link.removeAttribute('aria-busy');
      link.removeAttribute('aria-disabled');
      this.#isLoadingMore = false;
    }
  }

  /**
   * Each card sets its customizer data in an inline <script>. Scripts from parsed
   * HTML don't run when added to the page, so they're replaced with fresh ones.
   */
  #runInlineScripts(root) {
    for (const parsedScript of root.querySelectorAll('script')) {
      const script = document.createElement('script');
      for (const { name, value } of parsedScript.attributes) script.setAttribute(name, value);
      script.textContent = parsedScript.textContent;
      parsedScript.replaceWith(script);
    }
  }

  #queueRender(card) {
    const run = () => this.#renderCard(card);
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 500 });
    } else {
      setTimeout(run, 0);
    }
  }

  /**
   * Render a single card's canvas preview by invoking the live customizer's
   * window.updateCanvas with first-of-each selection picked from the product's
   * vue-data globals (emitted inline by the card snippet).
   */
  async #renderCard(card) {
    if (!this.isConnected || !card.isConnected) return;

    const canvas = card.querySelector('[data-preview-canvas]');
    if (!canvas) return;

    const varName = card.dataset.customizerDataVar;
    if (!varName) {
      console.warn('[personalized-grid] Card missing data-customizer-data-var', card);
      return;
    }

    // Wait up to 3s for vue-canvas.js and customizerData to be present. The
    // inline <script> from vue-data runs during HTML parsing; vue-canvas.js is
    // <script defer>, so it may arrive slightly after our module parses.
    const ready = await this.#waitForCustomizerRuntime(varName, 3000);
    if (!ready) {
      console.warn('[personalized-grid] Customizer runtime not ready for', varName);
      return;
    }

    const data = window[varName];
    if (!data || !data.options) {
      console.warn('[personalized-grid] Missing or malformed customizerData', varName, data);
      return;
    }

    // Pick first-of-each (mirrors snippets/vue-script.liquid initLabel line 1000).
    const selection = {
      set: (data.options.sets && data.options.sets[0]) || null,
      color: (data.options.colors && data.options.colors[0]) || null,
      color_alt: (data.options.color_alts && data.options.color_alts[0]) || null,
      font: (data.options.fonts && data.options.fonts[0]) || null,
    };

    // Size canvas internally (fixed square) and scale ctx so updateCanvas's
    // 2000x2000 coordinate space renders into our smaller buffer.
    canvas.width = PREVIEW_INTERNAL_SIZE;
    canvas.height = PREVIEW_INTERNAL_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scale = PREVIEW_INTERNAL_SIZE / CUSTOMIZER_COORD_SPACE;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    // Build the Image objects updateCanvas expects on data.label. Wait for
    // all three to finish loading (success or error) before the initial draw.
    // Mirrors the live customizer's Vue init pattern (see snippets/vue-script.liquid
    // lines 1395-1418) where bg + overlay are awaited before updateCustomizer fires.
    const bgImg = this.#makeImage(data.backgroundImage);
    const overlayImg = this.#makeImage(data.overlayImage);
    const bg2Img = this.#makeImage(data.backgroundImage2);

    const labelType = (data.label && data.label.type) || '';
    const labelSize = (data.label && data.label.size) || '';

    // Mini and Mini TagPals labels only support a single text line, regardless
    // of the `custom.lines` metafield (mirrors maxTextLines in vue-script.liquid).
    const singleLineSizes = ['mini', 'miniTagPals'];
    const maxTextLines = singleLineSizes.includes(labelSize) ? 1 : data.textLines || 2;

    const label = {
      type: labelType,
      size: labelSize,
      backgroundImage: bgImg,
      backgroundImage2: bg2Img,
      overlayImage: overlayImg,
      has_back: false,
      defaults: { Line_1: 'Suzie Q', Letter: 'S' },
      // Print product (AL SCRIPT template) for the first variant — the canvas uses its exact geometry.
      productCode: Object.values(data.productCodes || {})[0] || null,
    };

    // Build texts matching the Vue customizer's post-initLabel state.
    // Orbit labels use Letter='x' by default (hides the letter icon per vue-script.liquid line 1007).
    const texts = { Line_1: label.defaults.Line_1 };
    if (labelType === 'orbit') {
      texts.Letter = 'x';
    } else if (labelType === 'monogram') {
      texts.Letter = label.defaults.Letter;
    }

    // Photo object in the shape the customizer expects (vue-script.liquid line 107).
    // No upload on preview cards — the renderer's drawPhoto branches handle null upload.
    const photo = { upload: null, x: 0, y: 0, z: 0 };

    await this.#waitForImages([bgImg, bg2Img, overlayImg]);

    if (!this.isConnected || !card.isConnected) return;
    if (typeof window.updateCanvas !== 'function') {
      console.warn('[personalized-grid] window.updateCanvas unavailable when ready to draw');
      return;
    }

    // Reveal the canvas BEFORE calling updateCanvas. updateCanvas kicks off
    // async image loads for per-area graphics (bagtag/icon/photo/shoepals/orbit-multi)
    // and may throw on the first call because those images haven't completed yet.
    // It schedules recursive updateCanvas calls in their onload handlers which
    // paint progressively. If we only reveal on a successful first draw, those
    // async re-paints would be invisible. The scene background is already drawn
    // by this point, so the canvas always has meaningful content from frame one.
    card.dataset.previewRendered = 'true';

    // Store the render state so shared preview inputs can re-draw the card by
    // mutating `texts` in place (the onload callbacks registered by updateCanvas
    // capture `data` by reference, so they'll pick up mutated texts on the next fire).
    this.#cardRenderStates.set(card, { ctx, label, selection, texts, photo, maxTextLines });

    // If the visitor has already typed into the preview inputs before this card
    // lazy-rendered, apply those texts immediately.
    this.#applyCurrentPreviewTextsTo(card);

    try {
      window.updateCanvas(ctx, { label, selection, texts, photo });
    } catch (err) {
      // Expected for label types with async-loaded graphics; subsequent onload
      // callbacks will complete the rendering.
      console.debug('[personalized-grid] updateCanvas first call threw (awaiting async images)', err?.message, varName);
    }
  }

  /**
   * After the slide-over's Vue app mounts, push the grid's shared preview texts
   * into the app's reactive state. Reads directly from the live input elements
   * (not localStorage) to avoid a race with the 120ms debounced storage write
   * — a visitor who types "Alex" and immediately clicks Personalize would
   * otherwise get the pre-type value. Falls back to localStorage for the case
   * where the input elements aren't rendered (show_preview_inputs=false).
   *
   * Polls briefly for window.customizerApp[sectionId] because the Vue app is
   * registered inside the section's init IIFE that runs during script execution.
   */
  /**
   * Push the grid's shared preview texts into the slide-over's Vue customizer.
   *
   * We do this through Vue's v-model: find the rendered `<input id="text-Line_1">`
   * in the panel body, set `.value`, dispatch a real `input` event. Vue's reactive
   * `v-model="texts[key]"` catches it and propagates to `this.texts.Line_1`, which
   * triggers any downstream watchers and updateCustomizer() via the normal path.
   *
   * Polls briefly because the inputs are rendered by Vue after mount.
   */
  #pushStoredTextsToVueApp(sectionId) {
    const live = this.#getPreviewInputValues();
    const stored = readStoredPreviewTexts();
    const texts = {
      Line_1: live.Line_1 || stored.Line_1,
      Line_2: live.Line_2 || stored.Line_2,
    };
    if (!texts.Line_1 && !texts.Line_2) return;

    const panel = this.#panel();
    if (!panel) return;

    const started = Date.now();
    const tryApply = () => {
      // Input ids are now suffixed with the section id (text-Line_1-<sectionId>)
      const line1Input = panel.querySelector('input[id^="text-Line_1"]');
      if (line1Input) {
        this.#setNativeInputValue(line1Input, texts.Line_1 || '');
        if (texts.Line_2) {
          const line2Input = panel.querySelector('input[id^="text-Line_2"]');
          if (line2Input) this.#setNativeInputValue(line2Input, texts.Line_2);
        }
        return;
      }
      if (Date.now() - started < 2000) setTimeout(tryApply, 50);
    };
    tryApply();
  }

  /**
   * Set an <input>'s value in a way Vue's v-model observes. Setting `.value`
   * directly bypasses the native property setter Vue has patched, so we use
   * the prototype descriptor to write through the real setter, then dispatch
   * a bubbling `input` event. This is the standard React/Vue-compatible trick
   * for programmatic input changes.
   */
  #setNativeInputValue(input, value) {
    const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    if (descriptor && descriptor.set) {
      descriptor.set.call(input, value);
    } else {
      input.value = value;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // --- Shared preview text inputs ---

  #wirePreviewInputs() {
    const inputs = this.querySelectorAll('[data-preview-line]');
    if (!inputs.length) return;

    // Hydrate inputs from localStorage so a previously typed name persists across reloads / navigations.
    const stored = readStoredPreviewTexts();
    inputs.forEach((inp) => {
      const key = inp.dataset.previewLine === '2' ? 'Line_2' : 'Line_1';
      if (stored[key]) inp.value = stored[key];
    });

    this.#previewInputHandler = (event) => {
      // Strip unprintable characters (emojis, etc.) in place so the user sees
      // them disappear immediately and they never reach the canvas or storage.
      const input = event?.target;
      if (input && typeof input.value === 'string') {
        const sanitized = sanitizePrintable(input.value);
        if (input.value !== sanitized) {
          const cursor = input.selectionStart;
          const delta = input.value.length - sanitized.length;
          input.value = sanitized;
          if (cursor != null) {
            const pos = Math.max(0, cursor - delta);
            input.setSelectionRange(pos, pos);
          }
        }
      }
      // Persist synchronously on every keystroke so clicking Personalize right
      // after typing doesn't race the canvas-redraw debounce.
      writeStoredPreviewTexts(this.#getPreviewInputValues());
      clearTimeout(this.#previewInputDebounce);
      this.#previewInputDebounce = setTimeout(() => this.#applyCurrentPreviewTextsToAll(), 120);
    };

    inputs.forEach((inp) => inp.addEventListener('input', this.#previewInputHandler));
  }

  #getPreviewInputValues() {
    const line1 = this.querySelector('[data-preview-line="1"]')?.value ?? '';
    const line2 = this.querySelector('[data-preview-line="2"]')?.value ?? '';
    return { Line_1: line1.trim(), Line_2: line2.trim() };
  }

  #applyCurrentPreviewTextsTo(card) {
    const state = this.#cardRenderStates.get(card);
    if (!state) return;
    this.#applyTextsToState(state, this.#getPreviewInputValues());
  }

  #applyCurrentPreviewTextsToAll() {
    const values = this.#getPreviewInputValues();
    for (const state of this.#cardRenderStates.values()) {
      this.#applyTextsToState(state, values);
    }
  }

  /**
   * Apply the shared text inputs to a single card's render state and redraw.
   * Mutates state.texts in place so in-flight image onload callbacks (which
   * captured the original `data` by reference inside updateCanvas) pick up the
   * new text on their next fire. Empty inputs fall back to the label's default
   * (e.g. "Suzie Q") so the canvas stays visually meaningful.
   */
  #applyTextsToState(state, { Line_1, Line_2 }) {
    if (!state || !state.ctx || !state.label) return;
    state.texts.Line_1 = Line_1 || state.label.defaults.Line_1;
    // One-line labels (mini, mini TagPals) ignore Line 2 entirely.
    if (Line_2 && state.maxTextLines >= 2) {
      state.texts.Line_2 = Line_2;
    } else {
      delete state.texts.Line_2;
    }
    if (typeof window.updateCanvas !== 'function') return;
    try {
      window.updateCanvas(state.ctx, state);
    } catch (err) {
      // Async images may still be loading — their onload will redraw with the updated texts.
    }
  }

  /**
   * Wait for a batch of Image objects to reach a settled state (either loaded
   * or errored). Resolves immediately for images with no src or already-complete ones.
   */
  #waitForImages(images) {
    return Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (!img || !img.src) return resolve();
            if (img.complete && img.naturalWidth > 0) return resolve();
            const done = () => {
              img.removeEventListener('load', done);
              img.removeEventListener('error', done);
              resolve();
            };
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          })
      )
    );
  }

  /**
   * Returns a non-crossorigin-tainted <img> for the given URL (or a blank one
   * if the URL is empty).
   */
  #makeImage(url) {
    const img = new Image();
    img.setAttribute('crossorigin', 'anonymous');
    if (url) img.src = url;
    return img;
  }

  /**
   * Poll window for updateCanvas AND the per-card data var. Resolves true
   * when both are present, false on timeout.
   */
  #waitForCustomizerRuntime(varName, timeoutMs) {
    return new Promise((resolve) => {
      const started = Date.now();
      const check = () => {
        if (typeof window.updateCanvas === 'function' && window[varName]) {
          resolve(true);
          return;
        }
        if (Date.now() - started > timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(check, 50);
      };
      check();
    });
  }

  // --- Slide-over open/close ---

  #wireOpenTriggers() {
    this.#clickDelegate = (event) => {
      const trigger = event.target.closest('[data-customize-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      const card = trigger.closest('.personalized-grid__card');
      if (!card) return;
      // Let cmd/ctrl/shift/middle-click fall through so the title's <a href>
      // still works for "open in new tab" to the product page.
      if (event.type === 'click' && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) {
        return;
      }
      event.preventDefault();
      this.#openPanelForCard(card, trigger);
    };
    this.addEventListener('click', this.#clickDelegate);

    // Keyboard activation for non-button triggers (media div with role="button")
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('[data-customize-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      if (trigger.tagName === 'BUTTON' || trigger.tagName === 'A') return;
      event.preventDefault();
      const card = trigger.closest('.personalized-grid__card');
      if (card) this.#openPanelForCard(card, trigger);
    });
  }

  #openPanelForCard(card, trigger) {
    const productUrl = card.dataset.productUrl;
    if (!productUrl) {
      console.warn('[personalized-grid] Card missing data-product-url', card);
      return;
    }
    this.#panelController?.open(productUrl, trigger);
  }

  #panel() {
    return this.querySelector('[data-personalized-panel]');
  }

}

if (!customElements.get('personalized-grid-component')) {
  customElements.define('personalized-grid-component', PersonalizedGridComponent);
}

// --- Shopify theme editor lifecycle ---
// Close any open panel when its section is being unloaded by the editor.
if (typeof document !== 'undefined') {
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;
    const root = document.querySelector(
      `personalized-grid-component[data-section-id="${sectionId}"]`
    );
    root?.querySelector('[data-personalized-panel-close]')?.click();
  });
}
